import * as React from 'react';

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@crm/shared/lib/styles/utils';
import { Button } from '@crm/shared/ui/Button';

const Dialog = ({ ...props }: DialogPrimitive.Root.Props) => (
    <DialogPrimitive.Root data-slot="dialog" {...props} />
);

const DialogTrigger = ({ ...props }: DialogPrimitive.Trigger.Props) => (
    <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
);

const DialogPortal = ({ ...props }: DialogPrimitive.Portal.Props) => (
    <DialogPrimitive.Portal data-slot="dialog-portal" className="crm-portal" {...props} />
);

const DialogClose = ({ ...props }: DialogPrimitive.Close.Props) => (
    <DialogPrimitive.Close data-slot="dialog-close" {...props} />
);

const DialogOverlay = ({ className, ...props }: DialogPrimitive.Backdrop.Props) => (
    <DialogPrimitive.Backdrop
        data-slot="dialog-overlay"
        className={cn(
            'fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
            className
        )}
        {...props}
    />
);

const DialogContent = ({
    className,
    children,
    showCloseButton = true,
    closeBtnStyles,
    ...props
}: DialogPrimitive.Popup.Props & {
    showCloseButton?: boolean;
    closeBtnStyles?: string;
}) => {
    const { t } = useTranslation();

    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Popup
                data-slot="dialog-content"
                className={cn(
                    'fixed top-1/2 left-1/2 z-50 grid w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                    className
                )}
                {...props}
            >
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        render={
                            <Button
                                variant="ghost"
                                className={cn('absolute top-2 right-2', closeBtnStyles)}
                                size="icon-sm"
                            />
                        }
                    >
                        <XIcon />
                        <span className="sr-only">{t('button.close.text')}</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Popup>
        </DialogPortal>
    );
};

const DialogHeader = ({ className, ...props }: React.ComponentProps<'div'>) => (
    <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
);

const DialogFooter = ({
    className,
    showCloseButton = false,
    children,
    ...props
}: React.ComponentProps<'div'> & {
    showCloseButton?: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <div
            data-slot="dialog-footer"
            className={cn(
                '-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end',
                className
            )}
            {...props}
        >
            {children}
            {showCloseButton && (
                <DialogPrimitive.Close render={<Button variant="outline" />}>
                    {t('button.close.text')}
                </DialogPrimitive.Close>
            )}
        </div>
    );
};

const DialogTitle = ({ className, ...props }: DialogPrimitive.Title.Props) => (
    <DialogPrimitive.Title
        data-slot="dialog-title"
        className={cn('font-heading text-base leading-none font-medium', className)}
        {...props}
    />
);

const DialogDescription = ({ className, ...props }: DialogPrimitive.Description.Props) => (
    <DialogPrimitive.Description
        data-slot="dialog-description"
        className={cn(
            'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
            className
        )}
        {...props}
    />
);

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
